// Section 4: Ratio, Proportion & Rates of Change — pages 77-98
// Grade9Ready — GCSE Maths Topic Data
// Improved and corrected version

const PAGE_IMAGES = {
  p78_79: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/wnm47ior_Page78-79.png",
  p80_81: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/6yerpv7i_Page80-81.png",
  p82_83: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/ijnvm1wn_Page82-83.png",
  p84_85: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/qpb843u3_Page84-85.png",
  p86_87: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/sa30y87g_Page86-87.png",
  p88_89: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/90wejxjr_Page88-89.png",
  p90_91: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/5nsqilvo_Page90-91.png",
  p92_93: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/zy7x10jz_Page92-93.png",
  p94_95: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/8tlpg1v4_Page94-95.png",
  p96_97: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/he6qij6j_Page96-97.png",
};

export const ratioTopics = [
  {
    id: "4.1",
    title: "Simplifying Ratios",
    slug: "simplifying-ratios",
    pageRef: "Pages 77-78",
    difficulty: "foundation",
    gradeRange: "3-5",
    learn: {
      explanation:
        "Ratios compare quantities. A ratio can be simplified in the same way as a fraction: divide every part by the same number. Before simplifying, make sure all parts are written in the same unit.",
      keyPoints: [
        "Simplify by dividing every part by the HCF",
        "Ratios must be in the same unit before simplifying",
        "Decimals: multiply all parts by 10, 100 or 1000 first",
        "Fractions: multiply all parts by a common denominator first",
        "1:n form means the first part must be 1",
        "n:1 form means the second part must be 1",
      ],
      workedExamples: [
        {
          problem: "Simplify 25:15",
          steps: [
            { step: 1, instruction: "Find the HCF", working: "HCF of 25 and 15 = 5" },
            { step: 2, instruction: "Divide both parts by 5", working: "25 ÷ 5 : 15 ÷ 5 = 5:3" },
          ],
          answer: "5:3",
        },
        {
          problem: "Simplify 1.2:1.5",
          steps: [
            { step: 1, instruction: "Remove decimals", working: "1.2:1.5 = 12:15" },
            { step: 2, instruction: "Simplify by dividing by 3", working: "12 ÷ 3 : 15 ÷ 3 = 4:5" },
          ],
          answer: "4:5",
        },
        {
          problem: "Simplify 8cm : 48mm",
          steps: [
            { step: 1, instruction: "Convert to the same unit", working: "8cm = 80mm, so 80mm:48mm" },
            { step: 2, instruction: "Simplify", working: "80:48 ÷ 16 = 5:3" },
          ],
          answer: "5:3",
        },
        {
          problem: "Write 4:30 in the form 1:n",
          steps: [
            { step: 1, instruction: "Divide both parts by 4", working: "4 ÷ 4 : 30 ÷ 4 = 1:7.5" },
          ],
          answer: "1:7.5",
        },
      ],
      whyItWorks:
        "A simplified ratio keeps the same relationship between the quantities, but writes it in the clearest possible form.",
      misconceptions: [
        "Do not simplify different units until they have been converted.",
        "A ratio such as 2:3 does not mean 2 out of 3. It means 2 parts to 3 parts, so there are 5 parts altogether.",
        "Ratios can contain decimals in 1:n form, for example 1:2.5.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.1.w1",
          type: "text",
          question: "Simplify 7:63",
          answer: "1:9",
          hint1: "Find the HCF of 7 and 63.",
          hint2: "Divide both parts by 7.",
          hint3: "7 ÷ 7 : 63 ÷ 7 = 1:9",
        },
        {
          id: "4.1.w2",
          type: "text",
          question: "Simplify 2.2:5.5",
          answer: "2:5",
          hint1: "Multiply both parts by 10 first.",
          hint2: "22:55 can be divided by 11.",
          hint3: "22 ÷ 11 : 55 ÷ 11 = 2:5",
        },
      ],
      level1: [
        {
          id: "4.1.1",
          type: "text",
          question: "Simplify 18:24",
          answer: "3:4",
          hint1: "Find the HCF of 18 and 24.",
          hint2: "The HCF is 6.",
          hint3: "18 ÷ 6 : 24 ÷ 6 = 3:4",
        },
        {
          id: "4.1.2",
          type: "text",
          question: "Simplify 8cm : 48mm",
          answer: "5:3",
          hint1: "Convert 8cm to mm.",
          hint2: "8cm = 80mm, so the ratio is 80:48.",
          hint3: "80 ÷ 16 : 48 ÷ 16 = 5:3",
        },
        {
          id: "4.1.3",
          type: "text",
          question: "Simplify 0.45:0.75",
          answer: "3:5",
          hint1: "Multiply both parts by 100.",
          hint2: "45:75 can be simplified by dividing by 15.",
          hint3: "45 ÷ 15 : 75 ÷ 15 = 3:5",
        },
      ],
      level2: [
        {
          id: "4.1.4",
          type: "text",
          question: "Share £500 in the ratio 12:13.",
          answer: "£240 and £260",
          hint1: "Find the total number of parts.",
          hint2: "12 + 13 = 25 parts. £500 ÷ 25 = £20 per part.",
          hint3: "12 × £20 = £240 and 13 × £20 = £260.",
        },
        {
          id: "4.1.5",
          type: "text",
          question: "The ratio of boys to girls is 5:7. There are 36 students altogether. How many girls are there?",
          answer: "21",
          hint1: "There are 5 + 7 = 12 parts altogether.",
          hint2: "36 ÷ 12 = 3 students per part.",
          hint3: "Girls = 7 × 3 = 21.",
        },
      ],
      level3: [
        {
          id: "4.1.6",
          type: "text",
          question:
            "A red paint mixture uses red and white paint in the ratio 3:2. A painter uses 18 litres of red paint. How much white paint is needed?",
          answer: "12 litres",
          hint1: "3 parts represents 18 litres.",
          hint2: "1 part = 18 ÷ 3 = 6 litres.",
          hint3: "White paint = 2 × 6 = 12 litres.",
        },
      ],
    },
  },

  {
    id: "4.2",
    title: "Sharing in a Ratio",
    slug: "sharing-in-a-ratio",
    pageRef: "Pages 78-79",
    difficulty: "foundation",
    gradeRange: "4-6",
    learn: {
      explanation:
        "To share an amount in a ratio, add the ratio parts to find the total number of parts, divide the amount by the total parts, then multiply by each part of the ratio.",
      keyPoints: [
        "Add the ratio parts first",
        "Divide the total amount by the total number of parts",
        "Multiply the value of one part by each ratio number",
        "Check that the shares add back to the original total",
        "For part:whole ratios, be careful: the whole is already given in the ratio",
      ],
      workedExamples: [
        {
          problem: "Share £84 in the ratio 2:5.",
          image: PAGE_IMAGES.p78_79,
          steps: [
            { step: 1, instruction: "Find total parts", working: "2 + 5 = 7 parts" },
            { step: 2, instruction: "Find one part", working: "£84 ÷ 7 = £12" },
            { step: 3, instruction: "Find each share", working: "2 × £12 = £24 and 5 × £12 = £60" },
          ],
          answer: "£24 and £60",
        },
        {
          problem: "The ratio of red counters to total counters is 3:8. There are 40 counters. How many are red?",
          steps: [
            { step: 1, instruction: "Understand the ratio", working: "3:8 means red:total" },
            { step: 2, instruction: "Find one part", working: "40 ÷ 8 = 5" },
            { step: 3, instruction: "Find red counters", working: "3 × 5 = 15" },
          ],
          answer: "15 red counters",
        },
      ],
      whyItWorks:
        "A ratio splits a quantity into equal-sized parts. Once the value of one part is known, every share can be found.",
      misconceptions: [
        "In a part:part ratio such as 3:5, the total number of parts is 8, not 5.",
        "In a part:whole ratio such as 3:8, the whole is 8 parts, not 11 parts.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.2.w1",
          type: "text",
          question: "Share £60 in the ratio 1:2.",
          answer: "£20 and £40",
          hint1: "Total parts = 3.",
          hint2: "£60 ÷ 3 = £20 per part.",
          hint3: "1 part = £20 and 2 parts = £40.",
        },
      ],
      level1: [
        {
          id: "4.2.1",
          type: "text",
          question: "Share 72 sweets in the ratio 5:7.",
          answer: "30 and 42",
          hint1: "Total parts = 12.",
          hint2: "72 ÷ 12 = 6.",
          hint3: "5 × 6 = 30 and 7 × 6 = 42.",
        },
      ],
      level2: [
        {
          id: "4.2.2",
          type: "text",
          question:
            "The ratio of adults to children at an event is 4:9. There are 156 people at the event. How many children are there?",
          answer: "108",
          hint1: "Total parts = 4 + 9 = 13.",
          hint2: "156 ÷ 13 = 12.",
          hint3: "Children = 9 × 12 = 108.",
        },
      ],
      level3: [
        {
          id: "4.2.3",
          type: "text",
          question:
            "A prize is shared between A, B and C in the ratio 2:3:7. C receives £120 more than A. Find the total prize money.",
          answer: "£240",
          hint1: "C has 7 parts and A has 2 parts.",
          hint2: "The difference is 5 parts, and this equals £120.",
          hint3: "1 part = £24, so total = 12 parts = £288.",
          acceptedAnswers: ["£288", "288"],
        },
      ],
    },
  },

  {
    id: "4.3",
    title: "Direct & Inverse Proportion",
    slug: "direct-inverse-proportion",
    pageRef: "Pages 79-81",
    difficulty: "intermediate",
    gradeRange: "5-7",
    learn: {
      explanation:
        "Direct proportion means two quantities increase or decrease together at the same rate. Inverse proportion means one quantity increases while the other decreases so that the product stays constant.",
      keyPoints: [
        "Direct proportion: y = kx",
        "Inverse proportion: y = k/x",
        "Direct proportion graph: straight line through the origin",
        "Inverse proportion graph: reciprocal curve",
        "Direct method: divide to find one, then multiply",
        "Inverse method: multiply to find the total work, then divide",
      ],
      workedExamples: [
        {
          problem: "3 painters can paint 9 rooms in a day. How many rooms can 7 painters paint?",
          image: PAGE_IMAGES.p80_81,
          steps: [
            { step: 1, instruction: "Find one painter", working: "9 ÷ 3 = 3 rooms per painter" },
            { step: 2, instruction: "Multiply for 7 painters", working: "3 × 7 = 21 rooms" },
          ],
          answer: "21 rooms",
        },
        {
          problem: "3 farmers take 10 hours to complete a job. How long would 6 farmers take?",
          steps: [
            { step: 1, instruction: "Recognise inverse proportion", working: "More farmers means less time" },
            { step: 2, instruction: "Find total farmer-hours", working: "3 × 10 = 30 farmer-hours" },
            { step: 3, instruction: "Divide by the new number of farmers", working: "30 ÷ 6 = 5 hours" },
          ],
          answer: "5 hours",
        },
        {
          problem: "y is directly proportional to x. When x = 6, y = 18. Find y when x = 10.",
          steps: [
            { step: 1, instruction: "Use y = kx", working: "18 = 6k" },
            { step: 2, instruction: "Find k", working: "k = 3" },
            { step: 3, instruction: "Substitute x = 10", working: "y = 3 × 10 = 30" },
          ],
          answer: "30",
        },
      ],
      whyItWorks:
        "In direct proportion, the multiplier between the two quantities is constant. In inverse proportion, the product of the two quantities is constant.",
      misconceptions: [
        "Not every increasing relationship is direct proportion. The graph must pass through the origin.",
        "For inverse proportion, do not find one worker’s time by dividing. More workers should reduce the time.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.3.w1",
          type: "number",
          question: "6 baguettes cost £1.50. How much do 16 baguettes cost? Give your answer in pounds.",
          answer: "4",
          hint1: "Find the cost of 1 baguette.",
          hint2: "£1.50 ÷ 6 = £0.25.",
          hint3: "16 × £0.25 = £4.",
        },
      ],
      level1: [
        {
          id: "4.3.1",
          type: "number",
          question: "Alice buys 14 calculators for £35. How much would 22 calculators cost?",
          answer: "55",
          hint1: "Find the cost of 1 calculator.",
          hint2: "£35 ÷ 14 = £2.50.",
          hint3: "22 × £2.50 = £55.",
        },
      ],
      level2: [
        {
          id: "4.3.2",
          type: "number",
          question: "4 bakers take 5 hours to make a batch of cakes. How long would 10 bakers take?",
          answer: "2",
          hint1: "This is inverse proportion.",
          hint2: "4 × 5 = 20 baker-hours.",
          hint3: "20 ÷ 10 = 2 hours.",
        },
        {
          id: "4.3.3",
          type: "number",
          question: "y is directly proportional to x. When x = 8, y = 28. Find y when x = 12.",
          answer: "42",
          hint1: "Use y = kx.",
          hint2: "28 = 8k, so k = 3.5.",
          hint3: "y = 3.5 × 12 = 42.",
        },
      ],
      level3: [
        {
          id: "4.3.4",
          type: "number",
          question: "y is inversely proportional to x. When x = 4, y = 18. Find y when x = 9.",
          answer: "8",
          hint1: "Use y = k/x.",
          hint2: "18 = k/4, so k = 72.",
          hint3: "y = 72 ÷ 9 = 8.",
        },
      ],
    },
  },

  {
    id: "4.4",
    title: "Percentage Change",
    slug: "percentage-change",
    pageRef: "Pages 82-85",
    difficulty: "intermediate",
    gradeRange: "4-7",
    learn: {
      explanation:
        "Percentage change can be calculated using multipliers. A percentage increase uses a multiplier greater than 1. A percentage decrease uses a multiplier less than 1. Reverse percentage problems work backwards by dividing by the multiplier.",
      keyPoints: [
        "Increase multiplier: 1 + percentage/100",
        "Decrease multiplier: 1 − percentage/100",
        "Percentage change = change ÷ original × 100",
        "Reverse percentage: original = new value ÷ multiplier",
        "Simple interest adds the same amount each year",
        "Compound interest applies the percentage to the new amount each time",
      ],
      workedExamples: [
        {
          problem: "A hat costing £12 is reduced by 20%. Find the new price.",
          image: PAGE_IMAGES.p82_83,
          steps: [
            { step: 1, instruction: "Find the multiplier", working: "20% decrease = 1 − 0.20 = 0.80" },
            { step: 2, instruction: "Multiply", working: "£12 × 0.80 = £9.60" },
          ],
          answer: "£9.60",
        },
        {
          problem: "A car decreased by 40% to £7200. Find the original price.",
          image: PAGE_IMAGES.p84_85,
          steps: [
            { step: 1, instruction: "Find the multiplier", working: "40% decrease = 0.60" },
            { step: 2, instruction: "Divide by the multiplier", working: "£7200 ÷ 0.60 = £12,000" },
          ],
          answer: "£12,000",
        },
        {
          problem: "£500 is invested at 4% compound interest for 3 years. Find the final value.",
          steps: [
            { step: 1, instruction: "Find the multiplier", working: "4% increase = ×1.04" },
            { step: 2, instruction: "Apply for 3 years", working: "500 × 1.04³" },
            { step: 3, instruction: "Calculate", working: "500 × 1.124864 = £562.43" },
          ],
          answer: "£562.43",
        },
      ],
      whyItWorks:
        "The multiplier method converts a percentage into a decimal operation, making increases, decreases and reverse percentages quicker and more reliable.",
      misconceptions: [
        "A 20% decrease followed by a 20% increase does not return to the original value.",
        "For reverse percentages, divide by the multiplier, not by the percentage.",
        "Compound interest is not the same as simple interest.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.4.w1",
          type: "number",
          question: "Increase £400 by 15%.",
          answer: "460",
          hint1: "15% increase means multiplier 1.15.",
          hint2: "400 × 1.15.",
          hint3: "£460.",
        },
      ],
      level1: [
        {
          id: "4.4.1",
          type: "number",
          question: "A toy costs £4000. It is reduced by 15% in a sale. Find the sale price.",
          answer: "3400",
          hint1: "15% decrease means multiplier 0.85.",
          hint2: "4000 × 0.85.",
          hint3: "£3400.",
        },
        {
          id: "4.4.2",
          type: "number",
          question: "A house increased from £200,000 to £214,000. Find the percentage increase.",
          answer: "7",
          hint1: "Find the change.",
          hint2: "Change = £14,000.",
          hint3: "14000 ÷ 200000 × 100 = 7%.",
        },
      ],
      level2: [
        {
          id: "4.4.3",
          type: "number",
          question: "A video console costs £180 after a 40% discount. What was the original price?",
          answer: "300",
          hint1: "40% off means the new price is 60% of the original.",
          hint2: "Multiplier = 0.60.",
          hint3: "180 ÷ 0.60 = 300.",
        },
        {
          id: "4.4.4",
          type: "number",
          question: "£200 is invested at 3% simple interest for 5 years. How much interest is earned?",
          answer: "30",
          hint1: "Find one year's interest.",
          hint2: "200 × 0.03 = £6.",
          hint3: "5 × £6 = £30.",
        },
      ],
      level3: [
        {
          id: "4.4.5",
          type: "number",
          question: "£800 is invested at 5% compound interest for 2 years. Find the final amount.",
          answer: "882",
          hint1: "Use multiplier 1.05.",
          hint2: "800 × 1.05².",
          hint3: "800 × 1.1025 = £882.",
        },
        {
          id: "4.4.6",
          type: "number",
          question:
            "A population increases by 12% and is then 5600. What was the population before the increase?",
          answer: "5000",
          hint1: "12% increase means multiplier 1.12.",
          hint2: "5600 = original × 1.12.",
          hint3: "5600 ÷ 1.12 = 5000.",
        },
      ],
    },
  },

  {
    id: "4.5",
    title: "Scale, Maps & Recipes",
    slug: "scale-maps-recipes",
    pageRef: "Pages 86-88",
    difficulty: "intermediate",
    gradeRange: "4-7",
    learn: {
      explanation:
        "Scale and recipe problems use proportional reasoning. Multiply or divide all quantities by the same scale factor. In maps and scale drawings, convert between drawing length and real length carefully.",
      keyPoints: [
        "Scale factor = new amount ÷ original amount",
        "For recipes, multiply every ingredient by the same scale factor",
        "For maps, use the given scale to convert drawing distance to real distance",
        "Convert units carefully, especially cm to m or km",
        "A scale of 1:50 means 1 unit on the drawing represents 50 units in real life",
      ],
      workedExamples: [
        {
          problem: "A recipe serves 4 people and needs 300g flour. How much flour is needed for 10 people?",
          image: PAGE_IMAGES.p86_87,
          steps: [
            { step: 1, instruction: "Find the scale factor", working: "10 ÷ 4 = 2.5" },
            { step: 2, instruction: "Multiply the ingredient", working: "300g × 2.5 = 750g" },
          ],
          answer: "750g",
        },
        {
          problem: "A map has scale 1cm represents 5km. Two towns are 7.2cm apart on the map. Find the real distance.",
          steps: [
            { step: 1, instruction: "Use the scale", working: "1cm = 5km" },
            { step: 2, instruction: "Multiply", working: "7.2 × 5 = 36km" },
          ],
          answer: "36km",
        },
      ],
      whyItWorks:
        "Scale problems preserve the same multiplicative relationship between the original and the new quantity.",
      misconceptions: [
        "Do not add the difference repeatedly unless the relationship is additive. Most scale and recipe problems are multiplicative.",
        "A scale of 1:100 uses the same unit on both sides unless stated otherwise.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.5.w1",
          type: "number",
          question: "A recipe serves 3 people and uses 180g rice. How much rice is needed for 6 people?",
          answer: "360",
          hint1: "6 is double 3.",
          hint2: "Double 180g.",
          hint3: "360g.",
        },
      ],
      level1: [
        {
          id: "4.5.1",
          type: "number",
          question: "A map scale is 1cm = 2km. A route is 9cm on the map. What is the real distance in km?",
          answer: "18",
          hint1: "Each cm represents 2km.",
          hint2: "9 × 2.",
          hint3: "18km.",
        },
      ],
      level2: [
        {
          id: "4.5.2",
          type: "number",
          question:
            "A model car is made using a scale of 1:40. The model is 11cm long. What is the real car length in metres?",
          answer: "4.4",
          hint1: "Real length = 11 × 40 = 440cm.",
          hint2: "Convert 440cm to metres.",
          hint3: "440cm = 4.4m.",
        },
      ],
      level3: [
        {
          id: "4.5.3",
          type: "number",
          question:
            "A recipe for 8 people needs 600ml of milk. A chef has 2.1 litres of milk. What is the maximum number of people the recipe can serve?",
          answer: "28",
          hint1: "Convert 2.1 litres to 2100ml.",
          hint2: "Milk per person = 600 ÷ 8 = 75ml.",
          hint3: "2100 ÷ 75 = 28 people.",
        },
      ],
    },
  },

  {
    id: "4.6",
    title: "Converting Units",
    slug: "converting-units",
    pageRef: "Pages 88-93",
    difficulty: "foundation",
    gradeRange: "3-6",
    learn: {
      explanation:
        "Unit conversion means changing a measurement into an equivalent measurement in another unit. For length, multiply or divide by the conversion factor. For area, use the factor twice. For volume, use the factor three times.",
      keyPoints: [
        "Metric: 1km = 1000m, 1m = 100cm, 1cm = 10mm",
        "Mass: 1kg = 1000g",
        "Capacity: 1 litre = 1000ml = 1000cm³",
        "Approximate imperial: 1 inch ≈ 2.5cm, 1 mile ≈ 1.6km, 1kg ≈ 2.2lb",
        "Area: square the length conversion factor",
        "Volume: cube the length conversion factor",
      ],
      workedExamples: [
        {
          problem: "Convert 18 miles to kilometres.",
          image: PAGE_IMAGES.p88_89,
          steps: [
            { step: 1, instruction: "Use the conversion", working: "1 mile ≈ 1.6km" },
            { step: 2, instruction: "Multiply", working: "18 × 1.6 = 28.8" },
          ],
          answer: "28.8km",
        },
        {
          problem: "Convert 5m² to cm².",
          image: PAGE_IMAGES.p90_91,
          steps: [
            { step: 1, instruction: "Convert the length factor", working: "1m = 100cm" },
            { step: 2, instruction: "For area, use the factor twice", working: "1m² = 100 × 100 = 10,000cm²" },
            { step: 3, instruction: "Multiply", working: "5 × 10,000 = 50,000cm²" },
          ],
          answer: "50,000cm²",
        },
        {
          problem: "Convert 2m³ to cm³.",
          image: PAGE_IMAGES.p92_93,
          steps: [
            { step: 1, instruction: "Convert the length factor", working: "1m = 100cm" },
            { step: 2, instruction: "For volume, use the factor three times", working: "1m³ = 100 × 100 × 100 = 1,000,000cm³" },
            { step: 3, instruction: "Multiply", working: "2 × 1,000,000 = 2,000,000cm³" },
          ],
          answer: "2,000,000cm³",
        },
      ],
      whyItWorks:
        "Area and volume conversions grow faster than length conversions because they measure two-dimensional and three-dimensional space.",
      misconceptions: [
        "1m² is not 100cm². It is 10,000cm².",
        "1m³ is not 100cm³. It is 1,000,000cm³.",
        "Always check whether you are converting length, area or volume.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.6.w1",
          type: "number",
          question: "Convert 3.5cm to mm.",
          answer: "35",
          hint1: "1cm = 10mm.",
          hint2: "3.5 × 10.",
          hint3: "35mm.",
        },
        {
          id: "4.6.w2",
          type: "number",
          question: "Convert 2.8 litres to cm³.",
          answer: "2800",
          hint1: "1 litre = 1000cm³.",
          hint2: "2.8 × 1000.",
          hint3: "2800cm³.",
        },
      ],
      level1: [
        {
          id: "4.6.1",
          type: "number",
          question: "Convert 20km to miles using 1 mile ≈ 1.6km.",
          answer: "12.5",
          hint1: "You are converting km to miles.",
          hint2: "Divide by 1.6.",
          hint3: "20 ÷ 1.6 = 12.5 miles.",
        },
      ],
      level2: [
        {
          id: "4.6.2",
          type: "number",
          question: "Convert 60mph to km/h using 1 mile ≈ 1.6km.",
          answer: "96",
          hint1: "Each mile is about 1.6km.",
          hint2: "60 × 1.6.",
          hint3: "96km/h.",
        },
        {
          id: "4.6.3",
          type: "number",
          question: "Convert 3.2m² to cm².",
          answer: "32000",
          hint1: "1m² = 10,000cm².",
          hint2: "3.2 × 10,000.",
          hint3: "32,000cm².",
        },
      ],
      level3: [
        {
          id: "4.6.4",
          type: "number",
          question: "Convert 0.004m³ to cm³.",
          answer: "4000",
          hint1: "1m³ = 1,000,000cm³.",
          hint2: "0.004 × 1,000,000.",
          hint3: "4000cm³.",
        },
      ],
    },
  },

  {
    id: "4.7",
    title: "Best Buy, Density & Speed",
    slug: "best-buy-density-speed",
    pageRef: "Pages 94-97",
    difficulty: "intermediate",
    gradeRange: "4-7",
    learn: {
      explanation:
        "Best buy, density and speed are compound measure problems. A compound measure combines two quantities, such as miles per hour, grams per cubic centimetre, or pence per gram.",
      keyPoints: [
        "Best buy: compare price per unit or amount per pound",
        "Density = mass ÷ volume",
        "Mass = density × volume",
        "Volume = mass ÷ density",
        "Speed = distance ÷ time",
        "Distance = speed × time",
        "Time = distance ÷ speed",
        "Use consistent units before substituting into formulae",
      ],
      workedExamples: [
        {
          problem: "A car travels 90 miles at 60mph. How long does it take?",
          image: PAGE_IMAGES.p94_95,
          steps: [
            { step: 1, instruction: "Use the formula", working: "Time = distance ÷ speed" },
            { step: 2, instruction: "Substitute values", working: "90 ÷ 60 = 1.5 hours" },
            { step: 3, instruction: "Convert 0.5 hours", working: "0.5 hours = 30 minutes" },
          ],
          answer: "1 hour 30 minutes",
        },
        {
          problem: "Gold has density 19.3g/cm³ and volume 100cm³. Find the mass.",
          image: PAGE_IMAGES.p96_97,
          steps: [
            { step: 1, instruction: "Use the formula", working: "Mass = density × volume" },
            { step: 2, instruction: "Substitute values", working: "19.3 × 100 = 1930g" },
          ],
          answer: "1930g",
        },
        {
          problem: "Which is better value: 400ml for £1.60 or 1 litre for £3.00?",
          steps: [
            { step: 1, instruction: "Compare amount per pound", working: "400 ÷ 1.60 = 250ml per £1" },
            { step: 2, instruction: "Compare the second item", working: "1000 ÷ 3.00 = 333.3ml per £1" },
            { step: 3, instruction: "Choose the larger amount per pound", working: "1 litre gives more juice per £1" },
          ],
          answer: "1 litre is better",
        },
      ],
      whyItWorks:
        "Compound measures compare one quantity against another. This allows fair comparisons even when the original quantities are different.",
      misconceptions: [
        "For best buy, compare like with like: pence per gram, grams per pound, ml per penny, etc.",
        "For speed, time must be in one unit. 1 hour 30 minutes is 1.5 hours, not 1.30 hours.",
        "Density must use matching units, such as g/cm³ or kg/m³.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.7.w1",
          type: "choice",
          question: "Best buy: 400ml juice for £1.60 or 1 litre for £3.00?",
          options: ["400ml is better", "1 litre is better", "They're the same"],
          answer: "1 litre is better",
          hint1: "Convert 1 litre to 1000ml.",
          hint2: "400ml for £1.60 = 250ml per £1. 1000ml for £3 = 333ml per £1.",
          hint3: "The 1 litre bottle gives more juice per pound.",
        },
      ],
      level1: [
        {
          id: "4.7.1",
          type: "number",
          question: "Fred walks 5400m in 72 minutes. What is his speed in km/h?",
          answer: "4.5",
          hint1: "Convert 5400m to 5.4km and 72 minutes to 1.2 hours.",
          hint2: "Speed = distance ÷ time.",
          hint3: "5.4 ÷ 1.2 = 4.5km/h.",
        },
      ],
      level2: [
        {
          id: "4.7.2",
          type: "number",
          question: "A metal has mass 945g and volume 350cm³. Find its density in g/cm³.",
          answer: "2.7",
          hint1: "Density = mass ÷ volume.",
          hint2: "945 ÷ 350.",
          hint3: "2.7g/cm³.",
        },
        {
          id: "4.7.3",
          type: "number",
          question: "A cyclist travels at 18km/h for 40 minutes. How far does the cyclist travel in km?",
          answer: "12",
          hint1: "Convert 40 minutes to hours.",
          hint2: "40 minutes = 2/3 hour.",
          hint3: "18 × 2/3 = 12km.",
        },
      ],
      level3: [
        {
          id: "4.7.4",
          type: "number",
          question:
            "A block has density 7.8g/cm³ and mass 1950g. Find its volume in cm³.",
          answer: "250",
          hint1: "Volume = mass ÷ density.",
          hint2: "1950 ÷ 7.8.",
          hint3: "250cm³.",
        },
        {
          id: "4.7.5",
          type: "choice",
          question:
            "Which is better value: 750g for £2.10 or 1.2kg for £3.60?",
          options: ["750g for £2.10", "1.2kg for £3.60", "They are the same value"],
          answer: "750g for £2.10",
          hint1: "Convert 1.2kg to 1200g.",
          hint2: "£2.10 ÷ 750 = £0.0028 per gram. £3.60 ÷ 1200 = £0.003 per gram.",
          hint3: "The 750g pack has the lower price per gram.",
        },
      ],
    },
  },

  {
    id: "4.8",
    title: "Rates of Change & Gradients",
    slug: "rates-of-change-gradients",
    pageRef: "Pages 97-98",
    difficulty: "higher",
    gradeRange: "6-9",
    learn: {
      explanation:
        "A rate of change compares how quickly one quantity changes compared with another. On a graph, the gradient represents the rate of change. For a distance-time graph, gradient is speed. For a velocity-time graph, gradient is acceleration.",
      keyPoints: [
        "Gradient = change in y ÷ change in x",
        "On a distance-time graph, gradient = speed",
        "On a velocity-time graph, gradient = acceleration",
        "A steeper gradient means a faster rate of change",
        "A horizontal line has zero rate of change",
        "For curves, the instantaneous rate of change can be estimated using a tangent",
      ],
      workedExamples: [
        {
          problem:
            "On a distance-time graph, a car travels from 20km at 1pm to 80km at 3pm. Find its speed.",
          steps: [
            { step: 1, instruction: "Find change in distance", working: "80 − 20 = 60km" },
            { step: 2, instruction: "Find change in time", working: "3pm − 1pm = 2 hours" },
            { step: 3, instruction: "Use gradient", working: "60 ÷ 2 = 30km/h" },
          ],
          answer: "30km/h",
        },
        {
          problem:
            "Estimate the gradient of a curve at x = 4 using a tangent. The tangent passes through (2, 5) and (6, 17).",
          steps: [
            { step: 1, instruction: "Use the gradient formula", working: "Gradient = change in y ÷ change in x" },
            { step: 2, instruction: "Substitute the two points", working: "(17 − 5) ÷ (6 − 2)" },
            { step: 3, instruction: "Calculate", working: "12 ÷ 4 = 3" },
          ],
          answer: "3",
        },
      ],
      whyItWorks:
        "Gradient measures how much the vertical value changes for each one-unit increase in the horizontal value.",
      misconceptions: [
        "Do not calculate gradient as x change divided by y change.",
        "For curves, the gradient changes. Use a tangent for an estimate at a point.",
        "Units matter: if distance is in metres and time is in seconds, the rate is m/s.",
      ],
    },
    practice: {
      warmUp: [
        {
          id: "4.8.w1",
          type: "number",
          question: "Find the gradient of the line through (1, 4) and (5, 12).",
          answer: "2",
          hint1: "Gradient = change in y ÷ change in x.",
          hint2: "(12 − 4) ÷ (5 − 1).",
          hint3: "8 ÷ 4 = 2.",
        },
      ],
      level1: [
        {
          id: "4.8.1",
          type: "number",
          question:
            "A distance-time graph shows a journey from 10km to 70km in 3 hours. Find the speed in km/h.",
          answer: "20",
          hint1: "Find the change in distance.",
          hint2: "70 − 10 = 60km.",
          hint3: "60 ÷ 3 = 20km/h.",
        },
      ],
      level2: [
        {
          id: "4.8.2",
          type: "number",
          question:
            "A velocity-time graph shows velocity increasing from 4m/s to 20m/s in 8 seconds. Find the acceleration.",
          answer: "2",
          hint1: "Acceleration = change in velocity ÷ time.",
          hint2: "20 − 4 = 16m/s.",
          hint3: "16 ÷ 8 = 2m/s².",
        },
      ],
      level3: [
        {
          id: "4.8.3",
          type: "number",
          question:
            "A tangent to a curve passes through points (3, 11) and (9, 29). Estimate the gradient of the curve at the point of contact.",
          answer: "3",
          hint1: "Use the gradient of the tangent.",
          hint2: "(29 − 11) ÷ (9 − 3).",
          hint3: "18 ÷ 6 = 3.",
        },
      ],
    },
  },
];

export default ratioTopics;